import { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Card, Tag, Button, Dropdown, Modal, Input, Select, message } from 'antd';
import { PlayCircleOutlined, PauseCircleOutlined, ClockCircleOutlined, MoreOutlined, CloseCircleOutlined, RedoOutlined } from '@ant-design/icons';
import CommonTable from '../../components/commonTable';
import {
  useGetAddonSessionsQuery,
  useStartAddonSessionMutation,
  usePauseAddonSessionMutation,
  useResumeAddonSessionMutation,
  useCancelAddonSessionMutation,
  useResetAddonSessionMutation,
} from '../../services/membership';
import { useGetOpenTrainersQuery } from '../../services/trainer';
import { useAssignComplementaryTrainerSessionMutation, useUnassignComplementaryTrainerSessionMutation } from '../../services/complimentaryCoupon';
import usePermissions from '../../hooks/usePermissions';
import './styles.scss';

/* ─── Types ──────────────────────────────────────────────────────────── */

interface SessionRecord {
  _id?: string;
  sessionRecordId?: string;
  couponId?: string;
  sessionId?: string;
  sNo?: number;
  branch?: string;
  type?: string;
  isComplimentary?: boolean;
  addonType?: string;
  trainerName?: string;
  trainerId?: string;
  branchId?: string;
  sessions?: number;
  sessionDate?: string;
  status?: string;
  activeSeconds?: number;
}

interface TimerState {
  sessionId?: string;
  remainingSeconds?: number;
  status?: string;
}

/* ─── Helpers ────────────────────────────────────────────────────────── */

const formatTime = (seconds: number): string => {
  if (!seconds || seconds <= 0) return '00:00';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
};

/* ─── Component ──────────────────────────────────────────────────────── */

const AddOnSessionDetail = () => {
  const { membershipId } = useParams<{ membershipId: string }>();
  const location = useLocation();
  const { hasPermission } = usePermissions();

  // Both the membership tab and the addon-service tab route into this same page.
  // Membership sessions don't carry a pre-assigned trainer per row, so that flow
  // gets a "Select Trainer" picker; the addon-service flow is untouched.
  const isFromMembership = location.pathname.includes('/membership/add-on-session-detail');

  const { data: sessionData, isLoading } = useGetAddonSessionsQuery(membershipId as any);
  const [startAddonSession]  = useStartAddonSessionMutation();
  const [pauseAddonSession]  = usePauseAddonSessionMutation();
  const [resumeAddonSession] = useResumeAddonSessionMutation();
  const [cancelAddonSession, { isLoading: cancelling }] = useCancelAddonSessionMutation();
  const [resetAddonSession,  { isLoading: resetting }]  = useResetAddonSessionMutation();

  const selectedBranchId = useSelector((state: any) => state.branch.selectedBranch);
  const { data: trainersData } = useGetOpenTrainersQuery(
    { page: 1, limit: 100, branchId: selectedBranchId || undefined },
    { skip: !isFromMembership }
  );
  const allTrainers = useMemo(() => {
    const td = trainersData as any;
    const list = td?.data ?? td?.trainers;
    return Array.isArray(list) ? list : [];
  }, [trainersData]);

  // Trainer options for a row are limited to trainers specialized in that
  // row's Add-On Type (e.g. a "therapy" session only offers therapy trainers).
  const normalizeSpec = (s: string) => s.toLowerCase().replace(/_/g, ' ').trim();
  const getTrainerOptions = (addonType?: string) => {
    const filtered = addonType
      ? allTrainers.filter((t: any) => {
          const specs: string[] = t.specialization || [];
          return specs.some((s: string) => normalizeSpec(s) === normalizeSpec(addonType));
        })
      : allTrainers;
    return filtered
      .map((t: any) => ({ label: t?.user?.name ?? t?.name, value: t?._id ?? t?.id }))
      .filter((o: any) => o.label && o.value);
  };
  // Per-row trainer pick for membership sessions. `null` means explicitly
  // cleared (unassigned) by the user; a key absent from the map means "use
  // the record's own trainerId" (the trainer already assigned server-side).
  const [rowTrainerMap, setRowTrainerMap] = useState<Record<string, string | null>>({});
  const [assignComplementaryTrainerSession]   = useAssignComplementaryTrainerSessionMutation();
  const [unassignComplementaryTrainerSession] = useUnassignComplementaryTrainerSessionMutation();

  const handleAssignTrainer = async (record: SessionRecord, coachId: string) => {
    const rowKey = record._id ?? String(record.sNo);
    setRowTrainerMap(prev => ({ ...prev, [rowKey]: coachId }));
    const sessionId = record.sessionId ?? record._id;
    if (!membershipId || !record.couponId || !sessionId) return;
    try {
      await assignComplementaryTrainerSession({
        membershipId,
        couponId: record.couponId,
        sessionId,
        coachId,
      }).unwrap();
    } catch {
      /* error toast handled globally by badRequestHandler */
    }
  };

  // Resolves to the locally-picked trainer when the user has touched this row
  // (either picked one or cleared it), otherwise falls back to whatever the
  // record itself already has assigned server-side.
  const resolveTrainerId = (record: SessionRecord): string | undefined => {
    const sessionId = record._id ?? String(record.sNo);
    return sessionId in rowTrainerMap ? (rowTrainerMap[sessionId] ?? undefined) : record.trainerId;
  };

  const handleUnassignTrainer = async (record: SessionRecord) => {
    const rowKey = record._id ?? String(record.sNo);
    setRowTrainerMap(prev => ({ ...prev, [rowKey]: null }));
    const sessionId = record.sessionId ?? record._id;
    if (!membershipId || !record.couponId || !sessionId) return;
    try {
      await unassignComplementaryTrainerSession({
        membershipId,
        couponId: record.couponId,
        sessionId,
      }).unwrap();
    } catch {
      /* error toast handled globally by badRequestHandler */
    }
  };

  const summary     = (sessionData as any)?.summary    || {};
  const sessions    = (sessionData as any)?.data        || [];
  const activeTimer = (sessionData as any)?.activeTimer || null;

  const [timerState,       setTimerState]       = useState<TimerState>({});
  const [loadingSessionId, setLoadingSessionId] = useState<string | null>(null);
  const [cancelRecord,     setCancelRecord]     = useState<SessionRecord | null>(null);
  const [cancelReason,     setCancelReason]     = useState('');
  const [resetRecord,      setResetRecord]      = useState<SessionRecord | null>(null);
  const [resetReason,      setResetReason]      = useState('');
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startCountdown = (sessionId: string, remainingSeconds: number) => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    setTimerState({ sessionId, remainingSeconds, status: 'in_progress' });
    intervalRef.current = setInterval(() => {
      setTimerState(prev => {
        if ((prev.remainingSeconds ?? 0) <= 1) {
          clearInterval(intervalRef.current!);
          message.warning('Session time expired!');
          return { ...prev, remainingSeconds: 0, status: 'expired' };
        }
        return { ...prev, remainingSeconds: (prev.remainingSeconds ?? 0) - 1 };
      });
    }, 1000);
  };

  const showPausedState = (sessionId: string, remainingSeconds: number) => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    setTimerState({ sessionId, remainingSeconds, status: 'paused' });
  };

  useEffect(() => {
    if (activeTimer) {
      const { sessionId, status, remainingSeconds } = activeTimer;
      if (status === 'in_progress') startCountdown(sessionId, remainingSeconds);
      else if (status === 'paused') showPausedState(sessionId, remainingSeconds);
    } else {
      if (intervalRef.current) clearInterval(intervalRef.current);
      setTimerState({});
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [activeTimer]);

  const handleStartSession = async (record: SessionRecord) => {
    const sessionId = record._id ?? String(record.sNo);
    const trainerId = isFromMembership ? resolveTrainerId(record) : record.trainerId;
    if (!trainerId) {
      message.warning(isFromMembership ? 'Please select a trainer' : 'No trainer assigned to this session');
      return;
    }
    setLoadingSessionId(sessionId);
    try {
      await (startAddonSession as any)({
        membershipId,
        trainerId,
        type:                     record.isComplimentary ? 'complimentary' : 'paid',
        complementaryServiceType: record.addonType,
      }).unwrap();
    } catch (error) {
      console.error('Error starting session:', error);
    } finally {
      setLoadingSessionId(null);
    }
  };

  const handlePauseSession = async (record: SessionRecord) => {
    setLoadingSessionId(record._id ?? String(record.sNo));
    try {
      await (pauseAddonSession as any)(record._id).unwrap();
    } catch (error) {
      console.error('Error pausing session:', error);
    } finally {
      setLoadingSessionId(null);
    }
  };

  const handleResumeSession = async (record: SessionRecord) => {
    setLoadingSessionId(record._id ?? String(record.sNo));
    try {
      await (resumeAddonSession as any)(record._id).unwrap();
    } catch (error) {
      console.error('Error resuming session:', error);
    } finally {
      setLoadingSessionId(null);
    }
  };

  const handleCancelSession = (record: SessionRecord) => {
    setCancelReason('');
    setCancelRecord(record);
  };

  const handleResetSession = (record: SessionRecord) => {
    setResetReason('');
    setResetRecord(record);
  };

  const handleConfirmCancel = async () => {
    if (!cancelRecord) return;
    const sessionId = cancelRecord._id ?? cancelRecord.sessionRecordId;
    if (!sessionId) { message.error('This session has not started yet — nothing to cancel.'); return; }
    try {
      await cancelAddonSession({ sessionId, reason: cancelReason || undefined }).unwrap();
      setCancelRecord(null);
    } catch (error) {
      console.error('Error cancelling session:', error);
    }
  };

  const handleConfirmReset = async () => {
    if (!resetRecord) return;
    const sessionId = resetRecord._id ?? resetRecord.sessionRecordId;
    if (!sessionId) { message.error('This session has not started yet — nothing to reset.'); return; }
    try {
      await resetAddonSession({ sessionId, reason: resetReason || undefined }).unwrap();
      setResetRecord(null);
    } catch (error) {
      console.error('Error resetting session:', error);
    }
  };

  const columns = [
    {
      title:     'S.No',
      dataIndex: 'sNo',
      key:       'sNo',
      width:     80,
      align:     'center' as const,
    },
    {
      title:     'Branch',
      dataIndex: 'branch',
      key:       'branch',
      width:     200,
      render:    (text: string) => text || 'N/A',
    },
    {
      title:     'Type',
      dataIndex: 'type',
      key:       'type',
      width:     120,
      align:     'center' as const,
      render:    (type: string, record: SessionRecord) => (
        <Tag color={record.isComplimentary ? 'green' : 'blue'} style={{ textTransform: 'capitalize' }}>
          {type || 'N/A'}
        </Tag>
      ),
    },
    {
      title:     'Add-On Type',
      dataIndex: 'addonType',
      key:       'addonType',
      width:     150,
      render:    (text: string) =>
        text ? <span style={{ textTransform: 'capitalize' }}>{text.replace(/_/g, ' ')}</span> : 'N/A',
    },
    {
      title:     isFromMembership ? 'Assign Trainer' : 'Trainer Name',
      dataIndex: 'trainerName',
      key:       'trainerName',
      width:     180,
      render:    (text: string, record: SessionRecord) => {
        if (isFromMembership) {
          const currentId = resolveTrainerId(record);
          const options   = getTrainerOptions(record.addonType);
          // The assigned trainer may not be in the "open trainers" list (e.g. no
          // free slots) — keep them selectable/visible so the Select shows a
          // name instead of falling back to the raw id.
          const withCurrent = currentId && !options.some((o: { value: string }) => o.value === currentId) && record.trainerName
            ? [{ label: record.trainerName, value: currentId }, ...options]
            : options;
          return (
            <Select
              size="small"
              placeholder="Select trainer"
              value={currentId}
              onChange={(v: string | undefined) => { if (v) handleAssignTrainer(record, v); }}
              allowClear
              onClear={() => handleUnassignTrainer(record)}
              options={withCurrent}
              showSearch
              optionFilterProp="label"
              style={{ width: '100%', minWidth: 150 }}
            />
          );
        }
        return text || 'N/A';
      },
    },
    {
      title:     'Sessions',
      dataIndex: 'sessions',
      key:       'sessions',
      width:     120,
      align:     'center' as const,
      render:    (text: number) => text ?? '-',
    },
    {
      title:     'Session Date',
      dataIndex: 'sessionDate',
      key:       'sessionDate',
      width:     150,
      render:    (date: string) => {
        if (!date) return '-';
        const d = new Date(date);
        const dmy = `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
        return `${dmy} ${d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}`;
      },
    },
    {
      title:     'Status',
      dataIndex: 'status',
      key:       'status',
      width:     120,
      render:    (status: string) => {
        let color = 'default';
        if      (status === 'completed')  color = 'green';
        else if (status === 'in_progress') color = 'blue';
        else if (status === 'pending')    color = 'orange';
        else if (status === 'cancelled')  color = 'red';
        return (
          <Tag color={color} style={{ textTransform: 'capitalize' }}>
            {status === 'in_progress' ? 'In Progress' : status || 'N/A'}
          </Tag>
        );
      },
    },
    {
      title:     'Timer',
      dataIndex: '_id',
      key:       'timer',
      width:     150,
      align:     'center' as const,
      render:    (id: string, record: SessionRecord) => {
        if (timerState.sessionId === id && (timerState.remainingSeconds ?? 0) > 0) {
          return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, color: timerState.status === 'paused' ? '#faad14' : '#1890ff', fontWeight: 600, fontSize: 14 }}>
              <ClockCircleOutlined />
              <span>{formatTime(timerState.remainingSeconds ?? 0)}</span>
            </div>
          );
        }
        if (record.status === 'in_progress' && (record.activeSeconds ?? 0) >= 0) {
          return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, color: '#1890ff', fontWeight: 600, fontSize: 14 }}>
              <ClockCircleOutlined />
              <span>{formatTime(record.activeSeconds ?? 0)}</span>
            </div>
          );
        }
        return '-';
      },
    },
    {
      title:  'Actions',
      key:    'actions',
      width:  180,
      fixed:  'right' as const,
      render: (_: unknown, record: SessionRecord) => {
        const canStart  = hasPermission('9-ud-addon-svc-start-session');
        const canCancel = hasPermission('25-cancel');
        const canReset  = hasPermission('25-reset');

        if (!canStart && !canCancel && !canReset) return null;

        const sessionId     = record._id ?? String(record.sNo);
        const isRowLoading  = loadingSessionId === sessionId;
        const isFinished    = record.status === 'completed' || record.status === 'cancelled';

        const menuItems = [
          ...(canCancel
            ? [{ key: 'cancel', label: 'Cancel', icon: <CloseCircleOutlined />, danger: true, onClick: () => handleCancelSession(record) }]
            : []),
          ...(canReset && record.status === 'completed'
            ? [{ key: 'reset', label: 'Reset', icon: <RedoOutlined />, onClick: () => handleResetSession(record) }]
            : []),
        ];

        return (
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
            {canStart && (
              record.status === 'in_progress' ? (
                <Button icon={<PauseCircleOutlined />} size="small" className="paused-button" onClick={() => handlePauseSession(record)} loading={isRowLoading} danger>
                  Pause
                </Button>
              ) : record.status === 'paused' ? (
                <Button icon={<PlayCircleOutlined />} size="small" className="continue-button" onClick={() => handleResumeSession(record)} loading={isRowLoading}>
                  Continue
                </Button>
              ) : isFromMembership ? null : (
                <Button type="primary" icon={<PlayCircleOutlined />} size="small" onClick={() => handleStartSession(record)} disabled={isFinished} loading={isRowLoading}>
                  Start
                </Button>
              )
            )}
            {menuItems.length > 0 && (
              <Dropdown trigger={['click']} menu={{ items: menuItems }}>
                <Button icon={<MoreOutlined />} size="small" />
              </Dropdown>
            )}
          </div>
        );
      },
    },
  ];

  const pageTitle = isFromMembership ? 'Complimentary Coupon Details' : 'Add-On Session Details';

  // Membership-flow sessions and plain addon-service sessions are gated by
  // separate permissions (mirrors the split check in memberShipCard's click handler).
  if (!hasPermission(isFromMembership ? 'VIEW_ADDON_SESSION_DETAIL' : 'ADDON_SVC_VIEW_SESSION_DETAIL')) {
    return (
      <div className="add-on-session-detail-container">
        <div className="header">
          <h2>{pageTitle}</h2>
        </div>
        <div style={{ padding: 48, textAlign: 'center', color: 'var(--muted)' }}>
          You don't have permission to view session details.
        </div>
      </div>
    );
  }

  return (
    <div className="add-on-session-detail-container">
      <div className="header">
        <h2>{pageTitle}</h2>
      </div>

      {summary.totalSessions && (
        <Card className="session-summary">
          <div className="summary-stats">
            <div className="stat-item">
              <span className="stat-label">Total Sessions</span>
              <span className="stat-value">{summary.totalSessions || 0}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Used Sessions</span>
              <span className="stat-value used">{summary.usedSessions || 0}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Remaining Sessions</span>
              <span className="stat-value remaining">{summary.remainingSessions || 0}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Cancelled Sessions</span>
              <span className="stat-value cancelled">{summary.cancelledSessions || 0}</span>
            </div>
          </div>
        </Card>
      )}

      <CommonTable
        columns={columns}
        dataSource={sessions}
        loading={isLoading}
        scroll={{ x: 'max-content' }}
      />

      <Modal
        title="Cancel Session"
        open={!!cancelRecord}
        onCancel={() => setCancelRecord(null)}
        onOk={handleConfirmCancel}
        okText="Cancel Session"
        okType="danger"
        confirmLoading={cancelling}
      >
        <p>Are you sure you want to cancel this session?</p>
        <Input.TextArea
          rows={3}
          placeholder="Reason (optional)"
          value={cancelReason}
          onChange={(e) => setCancelReason(e.target.value)}
        />
      </Modal>

      <Modal
        title="Reset Session"
        open={!!resetRecord}
        onCancel={() => setResetRecord(null)}
        onOk={handleConfirmReset}
        okText="Reset Session"
        confirmLoading={resetting}
      >
        <p>Are you sure you want to reset this session?</p>
        <Input.TextArea
          rows={3}
          placeholder="Reason (optional)"
          value={resetReason}
          onChange={(e) => setResetReason(e.target.value)}
        />
      </Modal>
    </div>
  );
};

export default AddOnSessionDetail;
